import { AppDate } from "src/shared/utils/date-utils/AppDate";

/**
 * Today as YYYY-MM-DD from the browser's clock.
 *
 * `day_start` is a bare YYYY-MM-DD carrying no zone: import performance copies
 * `AdPerformanceRecord.day` verbatim from the network, which labels it in its own
 * ad account's timezone. We neither convert nor store that zone, so this clock and
 * the one that dated the rows disagree whenever viewer and account sit in
 * different zones. Known and accepted -- the gap only opens late at night, and the
 * correct cutoff is a backend fact not legible from here (MPN-1597).
 */
function today(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * Drop the current day from the daily cost rows (MPN-1567).
 *
 * `performance-by-day` is the spend/clicks/impressions/reach series, and EVERY
 * consumer of it computes a rate: CPC and CPM per network and over time, spend
 * and impressions per user, reach as a share, and the Performance Snapshot. None
 * of them plots it as a raw volume.
 *
 * A rate needs a finished day. Found at 7am on Rachel's campaign: 8/25 had 7
 * impressions and $0.50 of spend, an hour's delivery, which as a CPM read $71
 * against a normal $20-40 -- the smaller the denominator, the wilder the number.
 *
 * Here rather than in each chart because there are five of them and a sixth would
 * have to remember. Deliberately NOT applied to conversions, engagements or the
 * volume bars: those are counts, and a short bar for today is honest rather than
 * misleading. Hiding them is what made two real conversions vanish from the
 * Monitor page while the Population page showed them (MPN-1567).
 */
function completeCostDays(rows: any): any {
  if (!Array.isArray(rows)) return rows
  const now = today()
  return rows.filter((row: any) => !row?.day_start || row.day_start < now)
}

export class ChartSerializer {
 deserialize(payload: any) {
    const appDate = new AppDate()
    return {
      ...payload,
      charts: {
        ...payload.charts,
        // ?? [] on both, like every other key below. A caller that asks for a
        // subset of charts gets undefined for the ones it skipped, and an
        // unguarded .map() here took the whole Monitor page down with it. Harmless
        // while the client always requested all 19; MPN-1530 stops it doing that.
        "conversions-by-day": [
          {
            name: "Conversions",
            data: (payload.charts["conversions-by-day"] ?? []).map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.conversions ?? 0),
            ]),
          },
        ],
        "revenue-by-day": [
          {
            name: "Revenue",
            data: (payload.charts["revenue-by-day"] ?? []).map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.revenue ?? 0),
            ]),
          },
        ],
        "spend-conversions-by-day": Object.entries(
          (payload.charts["spend-conversions-by-day"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.spend_total ?? 0),
            ]),
        })),

        "impressions-by-network": Object.entries(
          (payload.charts["impressions-by-network"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.impression_total ?? 0),
            ]),
        })),

        "engagements-by-network": Object.entries(
          (payload.charts["engagements-by-network"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.engagement_total ?? 0),
            ]),
        })),

        "engagements-by-day": Object.entries(
          (payload.charts["engagements-by-day"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.engagement_total ?? 0),
            ]),
        })),

        "spend-by-network": Object.entries(
          (payload.charts["spend-by-network"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.spend_total ?? 0),
            ]),
        })),

        "audience-by-network": Object.entries(
          (payload.charts["audience-by-network"] ?? []).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network ?? "unknown"
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.audience_size ?? 0),
            ]),
        })),

        // Backend renamed this chart to spend-by-ad-group (rows keyed by
        // ad_group_name). Output key stays "spend-by-audience" so the Spend
        // view and its chart components are untouched.
        "spend-by-audience": Object.entries(
          (payload.charts["spend-by-ad-group"] ?? []).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.ad_group_name ?? "unknown"
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([audience, rows], idx) => ({
          id: idx + 1,
          name: audience,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.spend_total ?? 0),
            ]),
        })),

        "revenue-by-network": Object.entries(
          (payload.charts["revenue-by-network"] ?? []).filter((row: any) => row.reporting_network != null).reduce(
            (acc: Record<string, any[]>, row: any) => {
              const key = row.reporting_network
              ;(acc[key] ??= []).push(row)
              return acc
            },
            {},
          ),
        ).map(([network, rows], idx) => ({
          id: idx + 1,
          name: network,
          data: (rows as any[])
            .slice()
            .sort(
              (a, b) =>
                appDate.dateTimeInMs(a.day_start) -
                appDate.dateTimeInMs(b.day_start),
            )
            .map((val: any) => [
              appDate.dateTimeInMs(val.day_start),
              Number(val.revenue ?? 0),
            ]),
        })),

        // The one series on this page that every reader turns into a rate, so the
        // one series the current part-day has to be kept out of. See above.
        "performance-by-day": completeCostDays(payload.charts["performance-by-day"] ?? []),

        /*
            The same rows with today still in, for the readers that total money rather
            than divide it (MPN-2001).

            The rule above is about rates, and it says so: counts keep today, because a
            short bar is honest. The Spend tile is a count -- it is the campaign's money,
            not a per-something -- and it was reading the trimmed series, so it reported
            $990.73 beside a Spend By Network panel reading $1,004.36 on the same screen,
            with the Conversions tile next to it already including today.

            Two keys rather than a flag, so no existing reader changes behaviour by
            accident: every rate keeps pointing at the trimmed series.
        */
        "performance-by-day-all": payload.charts["performance-by-day"] ?? [],

        "conversions-by-journey-step": payload.charts["conversions-by-journey-step"] ?? [],
        "revenue-by-journey-step": payload.charts["revenue-by-journey-step"] ?? [],
        "last-file-load": payload.charts["last-file-load"] ?? {},
        "ad-performance": payload.charts["ad-performance"] ?? [],
      },
    }
  }
}